import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { whatsappLink } from "@/lib/whatsapp";

const JODHPUR = "https://images.pexels.com/photos/19160105/pexels-photo-19160105.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const UDAIPUR = "https://images.pexels.com/photos/27960113/pexels-photo-27960113.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const JAISALMER = "https://images.pexels.com/photos/35130760/pexels-photo-35130760.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const JAIPUR = "https://upload.wikimedia.org/wikipedia/commons/thumb/f/f6/Hawa_Mahal_%28The_Palace_of_Winds%29_in_Jaipur%2C_20191218_1201_9174.jpg/1280px-Hawa_Mahal_%28The_Palace_of_Winds%29_in_Jaipur%2C_20191218_1201_9174.jpg";
const MOUNTABU = "https://images.pexels.com/photos/533928/pexels-photo-533928.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PUSHKAR = "https://images.pexels.com/photos/3581916/pexels-photo-3581916.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const AJMER = "https://images.pexels.com/photos/3581369/pexels-photo-3581369.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const OSIAN = "https://upload.wikimedia.org/wikipedia/commons/thumb/7/7c/Sunset_over_Great_Thar_Desert_at_Osiyan_%284571264157%29.jpg/1280px-Sunset_over_Great_Thar_Desert_at_Osiyan_%284571264157%29.jpg";
const NAKODA = "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9b/Astapadh_temple%2C_Nakoda.jpg/1280px-Astapadh_temple%2C_Nakoda.jpg";

// Bento pattern: [span-2 x2, span-1 x3, span-1, span-1, span-2, span-1 x2]
const PACKAGES = [
  { name: "Jodhpur Sightseeing", nights: "1 Day", img: JODHPUR, big: true, tag: "Blue City" },
  { name: "Jaisalmer Golden Tour", nights: "2N / 3D", img: JAISALMER, big: true, tag: "Desert" },
  { name: "Udaipur Lake Tour", nights: "2N / 3D", img: UDAIPUR, tag: "Lake City" },
  { name: "Mount Abu", nights: "2N / 3D", img: MOUNTABU, tag: "Hill Station" },
  { name: "Jaipur Heritage", nights: "2N / 3D", img: JAIPUR, tag: "Pink City", big: true },
  { name: "Pushkar Tour", nights: "1N / 2D", img: PUSHKAR, tag: "Holy Lake" },
  { name: "Ajmer Sharif", nights: "1 Day", img: AJMER, tag: "Dargah" },
  { name: "Osian Desert Safari", nights: "1N / 2D", img: OSIAN, tag: "Sand Dunes" },
  { name: "Nakoda Ji Tour", nights: "1 Day", img: NAKODA, tag: "Jain Pilgrimage" },
];

export default function TourPackages() {
  return (
    <section id="packages" className="bg-white text-brand-ink py-24 md:py-32" data-testid="packages-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-blue">Chapter IV · Tour Packages</div>
            <h2 className="mt-4 font-display font-black text-5xl md:text-6xl leading-[1.02]">
              Curated <span className="italic text-brand-orange font-medium">Rajasthan</span> escapes.
            </h2>
          </div>
          <p className="max-w-md text-brand-ink/65">Ten hand-crafted itineraries by locals who grew up on these routes — palaces, dunes, lakes and lore.</p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 auto-rows-[260px] gap-4 md:gap-5">
          {PACKAGES.map((p, i) => (
            <motion.a
              key={p.name}
              href={whatsappLink(`Hi Mamta Tours, I'm interested in ${p.name} package.`)}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: (i % 6) * 0.05 }}
              className={`group relative rounded-3xl overflow-hidden bg-brand-cream ${p.big ? "md:col-span-2" : ""}`}
              data-testid={`package-${i}`}
            >
              <img src={p.img} alt={p.name} style={{ objectPosition: p.objectPos || "center" }} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute top-4 left-4 text-[10px] tracking-[0.24em] uppercase font-semibold bg-white/90 text-brand-blue rounded-full px-3 py-1">{p.tag}</div>
              <div className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/15 backdrop-blur-md text-white flex items-center justify-center border border-white/20 group-hover:bg-brand-orange group-hover:border-brand-orange transition-colors">
                <ArrowUpRight className="w-4 h-4" />
              </div>
              <div className="absolute left-5 right-5 bottom-5 text-white">
                <div className="text-[11px] tracking-[0.22em] uppercase font-semibold text-white/80">{p.nights}</div>
                <div className="font-display text-2xl md:text-3xl font-bold leading-tight mt-1">{p.name}</div>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
